import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, decimal, timestamp, time, jsonb, index, boolean, unique } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Canonical equipment types — single source of truth used by both client and server validation
export const EQUIPMENT_TYPES = [
  "Dry Van",
  "Flatbed",
  "Reefer",
  "Box Truck",
  "Cargo Van",
  "Step Deck",
  "Conestoga",
  "Hotshot",
  "Sprinter Van",
  "Power Only",
] as const;

export type EquipmentType = typeof EQUIPMENT_TYPES[number];

export const equipmentTypeEnum = z.enum(EQUIPMENT_TYPES);

// Session storage table for Replit Auth
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)],
);

// User storage table - supports both Replit Auth and legacy auth
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Replit Auth fields
  email: text("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  // Legacy fields (optional for backward compatibility)
  username: text("username").unique(),
  password: text("password"),
  // Application-specific fields
  userType: text("user_type"),
  company: text("company"),
  phone: text("phone"),
  membershipTier: text("membership_tier").notNull().default("Free"),
  billingCycle: text("billing_cycle").default("monthly"), // 'monthly' or 'annual'
  profileCompleted: text("profile_completed").notNull().default("false"),
  // Subscription and transaction tracking
  transactionCount: integer("transaction_count").notNull().default(0),
  transactionResetDate: timestamp("transaction_reset_date"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  stripeCustomerId: text("stripe_customer_id"),
  paymentSetupComplete: text("payment_setup_complete").notNull().default("false"),
  cancellationReason: text("cancellation_reason"),
  cancellationFeedback: text("cancellation_feedback"),
  // LTL-specific fields (for Carriers and Brokers)
  dotNumber: text("dot_number"),
  mcNumber: text("mc_number"),
  // Carrier-specific fields
  fleetSize: integer("fleet_size"),
  truckTypes: text("truck_types").array(),
  operatingRegions: text("operating_regions").array(),
  // Shipper-specific fields
  monthlyShipments: integer("monthly_shipments"),
  freightTypes: text("freight_types").array(),
  shippingLanes: text("shipping_lanes").array(),
  // Broker-specific fields
  yearsInBusiness: integer("years_in_business"),
  specializations: text("specializations").array(),
  commoditySpecializations: text("commodity_specializations").array(),
  hasBrokerBond: boolean("has_broker_bond").default(false),
  // Admin access
  isAdmin: text("is_admin").notNull().default("false"),
  // Verification status (pending, verified, rejected)
  verificationStatus: text("verification_status").notNull().default("pending"),
  verificationNote: text("verification_note"),
  isSuspended: boolean("is_suspended").notNull().default(false),
  suspensionReason: text("suspension_reason"),
  isIntrastate: boolean("is_intrastate").notNull().default(false),
  verifiedAt: timestamp("verified_at"),
  // Company branding
  companyLogo: text("company_logo"),
  companyDescription: text("company_description"),
  // Insurance info (for carriers)
  hasInsurance: boolean("has_insurance").default(false),
  insuranceProvider: text("insurance_provider"),
  // Acknowledgment (for shippers)
  hasShipperAcknowledgment: boolean("has_shipper_acknowledgment").default(false),
  // Test/demo account flag for sales demo account monitoring
  isTestAccount: boolean("is_test_account").notNull().default(false),
  // Email verification
  emailVerified: boolean("email_verified").notNull().default(false),
  emailVerificationToken: text("email_verification_token"),
  emailVerificationExpiry: timestamp("email_verification_expiry"),
  // Reputation fields
  upvoteCount: integer("upvote_count").notNull().default(0),
  completedShipments: integer("completed_shipments").notNull().default(0),
  // Activity tracking
  lastActiveAt: timestamp("last_active_at"),
  lastCapacityUpdate: timestamp("last_capacity_update"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
  // Referral program
  referralCode: text("referral_code").unique(),
  referredBy: text("referred_by"),
  billingCredits: integer("billing_credits").notNull().default(0),
  // Referral email rate-limit tracking
  referralEmailLastSentAt: timestamp("referral_email_last_sent_at"),
  // Multi-user account access — null means primary account owner
  accountOwnerId: text("account_owner_id"),
  // Verification sequence email tracking
  day6VerificationEmailSent: boolean("day6_verification_email_sent").notNull().default(false),
  monthlyPostCount: integer("monthly_post_count").notNull().default(0),
  monthlyPostResetAt: timestamp("monthly_post_reset_at"),
  // Shipper onboarding nudge email tracking
  partnerNudgeSentAt: timestamp("partner_nudge_sent_at"),
  loadNudgeSentAt: timestamp("load_nudge_sent_at"),
}, (table) => [
  index("IDX_users_user_type").on(table.userType),
  index("IDX_users_verification_status").on(table.verificationStatus),
  index("IDX_users_created_at").on(table.createdAt),
  index("IDX_users_email_verification_token").on(table.emailVerificationToken),
  index("IDX_users_membership_tier").on(table.membershipTier),
  index("IDX_users_referral_code").on(table.referralCode),
]);

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const upsertUserSchema = createInsertSchema(users, {
  id: z.string(),
  email: z.string().email().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  profileImageUrl: z.string().url().nullable(),
}).pick({
  id: true,
  email: true,
  firstName: true,
  lastName: true,
  profileImageUrl: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type UpsertUser = z.infer<typeof upsertUserSchema>;
export type User = typeof users.$inferSelect;

// Multi-user account access — links secondary members to an account owner
export const accountMembers = pgTable("account_members", {
  id: text("id").primaryKey().$defaultFn(() => crypto.randomUUID()),
  accountOwnerId: text("account_owner_id").notNull().references(() => users.id),
  memberUserId: text("member_user_id").notNull().references(() => users.id),
  role: text("role").notNull().default("member"),
  inviteToken: text("invite_token"),
  inviteTokenExpiresAt: timestamp("invite_token_expires_at"),
  inviteAcceptedAt: timestamp("invite_accepted_at"),
  invitedEmail: text("invited_email"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAccountMemberSchema = createInsertSchema(accountMembers).omit({
  id: true,
  createdAt: true,
});

export type InsertAccountMember = z.infer<typeof insertAccountMemberSchema>;
export type AccountMember = typeof accountMembers.$inferSelect;

// Profile completion schemas for each user type
export const shipperProfileSchema = z.object({
  userType: z.literal("Shipper"),
  company: z.string().min(1, "Company name is required"),
  phone: z.string().min(10, "Phone number is required"),
  monthlyShipments: z.number().int().min(1).optional(),
  freightTypes: z.array(z.string()).optional(),
  shippingLanes: z.array(z.string()).optional(),
  dotNumber: z.string().optional(),
  billingCycle: z.enum(["monthly", "annual", "free"]).optional(),
  companyDescription: z.string().max(500).nullable().optional(),
  hasShipperAcknowledgment: z.literal(true, {
    errorMap: () => ({ message: "You must acknowledge this before continuing" }),
  }),
});

export const carrierProfileSchema = z.object({
  userType: z.literal("Carrier"),
  company: z.string().min(1, "Company name is required"),
  phone: z.string().min(10, "Phone number is required"),
  dotNumber: z.string().min(1, "DOT number is required"),
  mcNumber: z.string().optional(),
  isIntrastate: z.boolean().optional().default(false),
  fleetSize: z.number().int().min(1, "Fleet size is required"),
  truckTypes: z.array(equipmentTypeEnum, {
    invalid_type_error: "Invalid equipment type provided",
  }).min(1, "At least one truck type is required"),
  operatingRegions: z.array(z.string()).min(1, "At least one region is required"),
  billingCycle: z.enum(["monthly", "annual", "free"]).optional(),
  companyDescription: z.string().max(500).nullable().optional(),
  specializations: z.array(z.string()).optional(),
  hasInsurance: z.literal(true, {
    errorMap: () => ({ message: "You must confirm active insurance to complete your profile" }),
  }),
  yearsInBusiness: z.number().int().min(0).optional(),
});

export const brokerProfileSchema = z.object({
  userType: z.literal("Broker"),
  company: z.string().min(1, "Company name is required"),
  phone: z.string().min(10, "Phone number is required"),
  mcNumber: z.string().optional(),
  dotNumber: z.string().min(1, "DOT number is required"),
  yearsInBusiness: z.number().int().min(0).optional(),
  specializations: z.array(z.string()).optional(),
  commoditySpecializations: z.array(z.string()).optional(),
  operatingRegions: z.array(z.string()).optional(),
  shippingLanes: z.array(z.string()).optional(),
  truckTypes: z.array(equipmentTypeEnum, {
    invalid_type_error: "Invalid equipment type provided",
  }).optional(),
  billingCycle: z.enum(["monthly", "annual", "free"]).optional(),
  companyDescription: z.string().max(500).nullable().optional(),
  hasBrokerBond: z.literal(true, {
    errorMap: () => ({ message: "You must confirm your broker bond status before continuing" }),
  }),
});

export type ShipperProfile = z.infer<typeof shipperProfileSchema>;
export type CarrierProfile = z.infer<typeof carrierProfileSchema>;
export type BrokerProfile = z.infer<typeof brokerProfileSchema>;

export const loads = pgTable("loads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  loadIdentifier: varchar("load_identifier", { length: 10 }).unique(),
  userId: varchar("user_id").notNull().references(() => users.id),
  origin: text("origin").notNull(),
  destination: text("destination").notNull(),
  weight: integer("weight").notNull(),
  dimensions: text("dimensions").notNull(),
  pickupDate: timestamp("pickup_date").notNull(),
  deliveryDate: timestamp("delivery_date"),
  price: decimal("price", { precision: 10, scale: 2 }),
  finalPrice: integer("final_price"),
  details: text("details"),
  status: text("status").notNull().default("Available"),
  
  // Freight Information
  handlingUnit: text("handling_unit").default("Pallet"),
  length: integer("length"),
  width: integer("width"),
  height: integer("height"),
  quantity: integer("quantity").default(1),
  stackable: boolean("stackable").default(false),
  hazardous: boolean("hazardous").default(false),
  
  // Location Types
  pickupLocationType: text("pickup_location_type").default("Business"),
  deliveryLocationType: text("delivery_location_type").default("Business"),
  
  // Shipping Services - Pickup
  liftgatePickup: boolean("liftgate_pickup").default(false),
  insidePickup: boolean("inside_pickup").default(false),
  
  // Shipping Services - Delivery
  liftgateDelivery: boolean("liftgate_delivery").default(false),
  insideDelivery: boolean("inside_delivery").default(false),
  deliveryAppointmentRequired: boolean("delivery_appointment_required").default(false),
  callBeforeDelivery: boolean("call_before_delivery").default(false),

  // Pickup window (set when shipper confirms pickup details)
  pickupTimeWindow: text("pickup_time_window"),

  // Appointment contact details (set when delivery appointment is required)
  appointmentContactName: text("appointment_contact_name"),
  appointmentContactPhone: text("appointment_contact_phone"),

  // Structured pickup and delivery scheduling and contact details
  pickupTimeStart: time("pickup_time_start"),
  pickupTimeEnd: time("pickup_time_end"),
  deliveryTimeStart: time("delivery_time_start"),
  deliveryTimeEnd: time("delivery_time_end"),
  pickupAddress: text("pickup_address"),
  deliveryAddress: text("delivery_address"),
  pickupContactName: text("pickup_contact_name"),
  pickupContactPhone: text("pickup_contact_phone"),
  deliveryContactName: text("delivery_contact_name"),
  deliveryContactPhone: text("delivery_contact_phone"),
  
  // BOL upload tracking
  bolUploaded: boolean("bol_uploaded").notNull().default(false),
  bolUploadedAt: timestamp("bol_uploaded_at"),

  // Batch 2 email-job tracking flags
  quoteReminderSent: boolean("quote_reminder_sent").notNull().default(false),
  pickupReminderSent: boolean("pickup_reminder_sent").notNull().default(false),
  bolReminderSent: boolean("bol_reminder_sent").notNull().default(false),
  deliveryEmailSent: boolean("delivery_email_sent").notNull().default(false),
  deliveryFollowUpSent: boolean("delivery_follow_up_sent").notNull().default(false),
  deliveryFollowUpCount: integer("delivery_follow_up_count").notNull().default(0),
  deliveryFollowUpLastSentAt: timestamp("delivery_follow_up_last_sent_at"),

  // Geocoded coordinates (populated asynchronously after creation)
  originLat: decimal("origin_lat", { precision: 10, scale: 7 }),
  originLng: decimal("origin_lng", { precision: 10, scale: 7 }),

  // Claim tracking for reputation system
  claimedByUserId: varchar("claimed_by_user_id").references(() => users.id),
  claimedAt: timestamp("claimed_at"),
  completedAt: timestamp("completed_at"),
  // External party assignment for broker-partial transactions
  externalCarrierId: varchar("external_carrier_id"),
  externalShipperId: varchar("external_shipper_id"),
  lastConfirmedAt: timestamp("last_confirmed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("IDX_loads_user_id").on(table.userId),
  index("IDX_loads_status").on(table.status),
  index("IDX_loads_created_at").on(table.createdAt),
  index("IDX_loads_identifier").on(table.loadIdentifier),
  index("IDX_loads_claimed_by_user_id").on(table.claimedByUserId),
  index("IDX_loads_status_created_at").on(table.status, table.createdAt),
  index("IDX_loads_pickup_date").on(table.pickupDate),
  // GIN pg_trgm indexes on origin/destination are managed via
  // migrations/0001_add_performance_indexes.sql (not expressible cleanly in Drizzle schema)
]);

export const insertLoadSchema = createInsertSchema(loads, {
  pickupDate: z.coerce.date(),
  deliveryDate: z.coerce.date().nullable(),
  price: z.string().optional(),
  details: z.string().optional(),
}).omit({
  id: true,
  loadIdentifier: true,
  createdAt: true,
  claimedByUserId: true,
  claimedAt: true,
  completedAt: true,
});

// Schema for frontend input (without userId - added on server)
export const createLoadSchema = insertLoadSchema.omit({ userId: true });

export type InsertLoad = z.infer<typeof insertLoadSchema>;
export type CreateLoad = z.infer<typeof createLoadSchema>;
export type Load = typeof loads.$inferSelect;

// Load items - multiple freight items per load
export const loadItems = pgTable("load_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  loadId: varchar("load_id").notNull().references(() => loads.id, { onDelete: "cascade" }),
  handlingUnit: text("handling_unit").notNull().default("Pallet"),
  length: integer("length"),
  width: integer("width"),
  height: integer("height"),
  weight: integer("weight").notNull(),
  quantity: integer("quantity").notNull().default(1),
  stackable: boolean("stackable").default(false),
  hazardous: boolean("hazardous").default(false),
  description: text("description"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("IDX_load_items_load_id").on(table.loadId),
]);

export const insertLoadItemSchema = createInsertSchema(loadItems).omit({
  id: true,
  createdAt: true,
});

// Schema for frontend input (without loadId - added on server)
export const createLoadItemSchema = insertLoadItemSchema.omit({ loadId: true });

export type InsertLoadItem = z.infer<typeof insertLoadItemSchema>;
export type CreateLoadItem = z.infer<typeof createLoadItemSchema>;
export type LoadItem = typeof loadItems.$inferSelect;

export const trucks = pgTable("trucks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id),
  origin: text("origin").notNull(),
  originZip: varchar("origin_zip", { length: 10 }),
  originCity: varchar("origin_city", { length: 100 }),
  originState: varchar("origin_state", { length: 50 }),
  destination: text("destination").notNull(),
  destinationZip: varchar("destination_zip", { length: 10 }),
  destinationCity: varchar("destination_city", { length: 100 }),
  destinationState: varchar("destination_state", { length: 50 }),
  capacity: integer("capacity").notNull(),
  dimensions: text("dimensions").notNull(),
  availableFrom: timestamp("available_from").notNull(),
  availableTo: timestamp("available_to").notNull(),
  pricePerMile: decimal("price_per_mile", { precision: 10, scale: 2 }),
  description: text("description"),
  status: text("status").notNull().default("Available"),
  equipmentType: text("equipment_type"),
  availablePallets: integer("available_pallets"),
  availableWeight: integer("available_weight"),
  availableLinearFeet: integer("available_linear_feet"),
  acceptsStackable: boolean("accepts_stackable").default(true),
  acceptsHazardous: boolean("accepts_hazardous").default(false),
  acceptsPartialLane: boolean("accepts_partial_lane").default(false),
  destinationFlexible: boolean("destination_flexible").default(false),
  lastConfirmedAt: timestamp("last_confirmed_at"),
  // Geocoded coordinates (populated asynchronously after creation)
  originLat: decimal("origin_lat", { precision: 10, scale: 7 }),
  originLng: decimal("origin_lng", { precision: 10, scale: 7 }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("IDX_trucks_user_id").on(table.userId),
  index("IDX_trucks_status").on(table.status),
  index("IDX_trucks_origin_city").on(table.originCity),
  index("IDX_trucks_destination_city").on(table.destinationCity),
  index("IDX_trucks_equipment_type").on(table.equipmentType),
  index("IDX_trucks_available_from").on(table.availableFrom),
  index("IDX_trucks_status_available_from").on(table.status, table.availableFrom),
  // GIN pg_trgm indexes on origin/destination (and originCity/destinationCity) are managed via
  // migrations/0001_add_performance_indexes.sql (not expressible cleanly in Drizzle schema)
]);

export const insertTruckSchema = createInsertSchema(trucks, {
  availableFrom: z.coerce.date(),
  availableTo: z.coerce.date(),
  pricePerMile: z.string().optional(),
  equipmentType: equipmentTypeEnum.nullable().optional(),
}).omit({
  id: true,
  createdAt: true,
});

// Schema for frontend input (without userId - added on server)
export const createTruckSchema = insertTruckSchema.omit({ userId: true });

export type InsertTruck = z.infer<typeof insertTruckSchema>;
export type CreateTruck = z.infer<typeof createTruckSchema>;
export type Truck = typeof trucks.$inferSelect;

export const messages = pgTable("messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  fromUserId: varchar("from_user_id").notNull().references(() => users.id),
  sentByUserId: varchar("sent_by_user_id").references(() => users.id),
  toUserId: varchar("to_user_id").notNull().references(() => users.id),
  loadId: varchar("load_id").references(() => loads.id),
  truckId: varchar("truck_id").references(() => trucks.id),
  message: text("message").notNull(),
  quotePrice: integer("quote_price"),
  quoteTransitTime: text("quote_transit_time"),
  quotePickupDate: timestamp("quote_pickup_date"),
  messageType: text("message_type").notNull().default("text"),
  claimInvitationStatus: text("claim_invitation_status"),
  attachmentUrl: text("attachment_url"),
  attachmentName: text("attachment_name"),
  attachmentType: text("attachment_type"),
  attachmentSize: integer("attachment_size"),
  readAt: timestamp("read_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("IDX_messages_from_user").on(table.fromUserId),
  index("IDX_messages_to_user").on(table.toUserId),
  index("IDX_messages_load_id").on(table.loadId),
  index("IDX_messages_read_at").on(table.readAt),
  index("IDX_messages_to_user_read_at").on(table.toUserId, table.readAt),
  index("IDX_messages_from_to_load").on(table.fromUserId, table.toUserId, table.loadId),
  index("IDX_messages_truck_id").on(table.truckId),
  index("IDX_messages_attachment_url").on(table.attachmentUrl),
]);

export const insertMessageSchema = createInsertSchema(messages).omit({
  id: true,
  readAt: true,
  createdAt: true,
});

export type InsertMessage = z.infer<typeof insertMessageSchema>;
export type Message = typeof messages.$inferSelect;

// Shipment confirmations - tracks mutual completion confirmations
export const shipmentConfirmations = pgTable("shipment_confirmations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  loadId: varchar("load_id").notNull().references(() => loads.id),
  userId: varchar("user_id").notNull().references(() => users.id),
  confirmationType: text("confirmation_type").notNull().default("manual"),
  confirmedAt: timestamp("confirmed_at").notNull().defaultNow(),
}, (table) => [
  index("IDX_shipment_confirmations_load_id").on(table.loadId),
  index("IDX_shipment_confirmations_user_load").on(table.userId, table.loadId),
]);

export const insertShipmentConfirmationSchema = createInsertSchema(shipmentConfirmations).omit({
  id: true,
  confirmedAt: true,
});

export type InsertShipmentConfirmation = z.infer<typeof insertShipmentConfirmationSchema>;
export type ShipmentConfirmation = typeof shipmentConfirmations.$inferSelect;

// Upvotes - blind submission system with reveal logic
export const upvotes = pgTable("upvotes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  loadId: varchar("load_id").notNull().references(() => loads.id),
  fromUserId: varchar("from_user_id").notNull().references(() => users.id),
  toUserId: varchar("to_user_id").notNull().references(() => users.id),
  status: text("status").notNull().default("pending"),
  submittedAt: timestamp("submitted_at").notNull().defaultNow(),
  revealedAt: timestamp("revealed_at"),
}, (table) => [
  index("IDX_upvotes_load_id").on(table.loadId),
  index("IDX_upvotes_from_user_load").on(table.fromUserId, table.loadId),
  index("IDX_upvotes_to_user_status").on(table.toUserId, table.status),
  index("IDX_upvotes_load_status").on(table.loadId, table.status),
]);

export const insertUpvoteSchema = createInsertSchema(upvotes).omit({
  id: true,
  submittedAt: true,
  revealedAt: true,
});

export type InsertUpvote = z.infer<typeof insertUpvoteSchema>;
export type Upvote = typeof upvotes.$inferSelect;

// Newsletter subscribers
export const newsletterSubscribers = pgTable("newsletter_subscribers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  subscribedAt: timestamp("subscribed_at").notNull().defaultNow(),
});

export const insertNewsletterSubscriberSchema = createInsertSchema(newsletterSubscribers).omit({
  id: true,
  subscribedAt: true,
});

export type InsertNewsletterSubscriber = z.infer<typeof insertNewsletterSubscriberSchema>;
export type NewsletterSubscriber = typeof newsletterSubscribers.$inferSelect;

// Contact form schema
export const contactFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  subject: z.string().min(2, "Subject must be at least 2 characters"),
  message: z.string().min(10, "Message must be at least 10 characters")
});

export type ContactFormData = z.infer<typeof contactFormSchema>;

// Contact submissions table for storing form submissions
export const contactSubmissions = pgTable("contact_submissions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  email: text("email").notNull(),
  subject: text("subject").notNull(),
  message: text("message").notNull(),
  status: text("status").notNull().default("new"), // new, read, replied
  createdAt: timestamp("created_at").notNull().defaultNow(),
  readAt: timestamp("read_at"),
  repliedAt: timestamp("replied_at"),
});

export const insertContactSubmissionSchema = createInsertSchema(contactSubmissions).omit({
  id: true,
  status: true,
  createdAt: true,
  readAt: true,
  repliedAt: true,
});

export type InsertContactSubmission = z.infer<typeof insertContactSubmissionSchema>;
export type ContactSubmission = typeof contactSubmissions.$inferSelect;

export const contactReplies = pgTable("contact_replies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  submissionId: varchar("submission_id").notNull().references(() => contactSubmissions.id),
  replyMessage: text("reply_message").notNull(),
  sentBy: varchar("sent_by").notNull().references(() => users.id),
  sentAt: timestamp("sent_at").notNull().defaultNow(),
});

export type ContactReply = typeof contactReplies.$inferSelect;

// Member Preferences - for notification matching
export const memberPreferences = pgTable("member_preferences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id).unique(),
  // Origin preferences - regions/cities they want to be notified about
  preferredOrigins: text("preferred_origins").array(),
  // Destination preferences
  preferredDestinations: text("preferred_destinations").array(),
  // Weight/capacity ranges
  minWeight: integer("min_weight"),
  maxWeight: integer("max_weight"),
  // Equipment type preferences
  equipmentTypes: text("equipment_types").array(),
  // Notification settings
  notificationsEnabled: boolean("notifications_enabled").default(true),
  emailNotifications: boolean("email_notifications").default(true),
  smsNotifications: boolean("sms_notifications").default(false),
  // Match score threshold (0-100) - only notify if match score is above this
  matchThreshold: integer("match_threshold").default(50),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("IDX_member_prefs_notifications_enabled").on(table.notificationsEnabled),
]);

export const insertMemberPreferencesSchema = createInsertSchema(memberPreferences).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateMemberPreferencesSchema = insertMemberPreferencesSchema.partial().omit({ userId: true });

export type InsertMemberPreferences = z.infer<typeof insertMemberPreferencesSchema>;
export type UpdateMemberPreferences = z.infer<typeof updateMemberPreferencesSchema>;
export type MemberPreferences = typeof memberPreferences.$inferSelect;

// Notifications - in-app notifications for members
export const notifications = pgTable("notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id),
  // Notification type: match_load, match_truck, system, message, claim_invitation
  type: text("type").notNull(),
  title: text("title").notNull(),
  message: text("message").notNull(),
  // Related entities for navigation
  loadId: varchar("load_id").references(() => loads.id),
  truckId: varchar("truck_id").references(() => trucks.id),
  relatedUserId: varchar("related_user_id").references(() => users.id),
  // Match score if this is a match notification
  matchScore: integer("match_score"),
  // Status
  read: boolean("read").default(false),
  dismissed: boolean("dismissed").default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("IDX_notifications_user_dismissed").on(table.userId, table.dismissed),
  index("IDX_notifications_user_read_dismissed").on(table.userId, table.read, table.dismissed),
  index("IDX_notifications_user_type_load").on(table.userId, table.type, table.loadId),
  index("IDX_notifications_user_created").on(table.userId, table.createdAt),
]);

export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  createdAt: true,
});

export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type Notification = typeof notifications.$inferSelect;

// Password reset tokens
export const passwordResetTokens = pgTable("password_reset_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id),
  token: varchar("token").notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertPasswordResetTokenSchema = createInsertSchema(passwordResetTokens).omit({
  id: true,
  createdAt: true,
  usedAt: true,
});

export type InsertPasswordResetToken = z.infer<typeof insertPasswordResetTokenSchema>;
export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;

// Login attempts tracking for account lockout
export const loginAttempts = pgTable("login_attempts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull(),
  ipAddress: text("ip_address"),
  success: boolean("success").notNull().default(false),
  attemptedAt: timestamp("attempted_at").notNull().defaultNow(),
}, (table) => [
  index("IDX_login_attempts_email").on(table.email),
  index("IDX_login_attempts_attempted_at").on(table.attemptedAt),
]);

export const insertLoginAttemptSchema = createInsertSchema(loginAttempts).omit({
  id: true,
  attemptedAt: true,
});

export type InsertLoginAttempt = z.infer<typeof insertLoginAttemptSchema>;
export type LoginAttempt = typeof loginAttempts.$inferSelect;

// Account lockout tracking
export const accountLockouts = pgTable("account_lockouts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  lockedAt: timestamp("locked_at").notNull().defaultNow(),
  unlocksAt: timestamp("unlocks_at").notNull(),
  failedAttempts: integer("failed_attempts").notNull().default(0),
});

export const insertAccountLockoutSchema = createInsertSchema(accountLockouts).omit({
  id: true,
  lockedAt: true,
});

export type InsertAccountLockout = z.infer<typeof insertAccountLockoutSchema>;
export type AccountLockout = typeof accountLockouts.$inferSelect;

// Analytics events for tracking platform activity
export const analyticsEvents = pgTable("analytics_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // Event identification
  eventType: varchar("event_type", { length: 100 }).notNull(),
  eventCategory: varchar("event_category", { length: 50 }).notNull(), // load, quote, claim, message, user
  // Actor information
  userId: varchar("user_id").references(() => users.id),
  userType: varchar("user_type", { length: 20 }), // Shipper, Carrier, Broker
  // Related entities
  loadId: varchar("load_id").references(() => loads.id),
  truckId: varchar("truck_id").references(() => trucks.id),
  messageId: varchar("message_id").references(() => messages.id),
  targetUserId: varchar("target_user_id").references(() => users.id), // e.g., recipient of quote
  // Event-specific data (flexible JSON)
  metadata: jsonb("metadata"), // { price: 1500, route: "NY-LA", ... }
  // Timestamps
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("IDX_analytics_events_event_type").on(table.eventType),
  index("IDX_analytics_events_event_category").on(table.eventCategory),
  index("IDX_analytics_events_user_id").on(table.userId),
  index("IDX_analytics_events_load_id").on(table.loadId),
  index("IDX_analytics_events_created_at").on(table.createdAt),
]);

export const insertAnalyticsEventSchema = createInsertSchema(analyticsEvents).omit({
  id: true,
  createdAt: true,
});

export type InsertAnalyticsEvent = z.infer<typeof insertAnalyticsEventSchema>;
export type AnalyticsEvent = typeof analyticsEvents.$inferSelect;

// Event type constants for consistency
export const ANALYTICS_EVENT_TYPES = {
  // Load events
  LOAD_POSTED: 'load_posted',
  LOAD_VIEWED: 'load_viewed',
  LOAD_UPDATED: 'load_updated',
  LOAD_DELETED: 'load_deleted',
  LOAD_CLAIMED: 'load_claimed',
  LOAD_COMPLETED: 'load_completed',
  // Truck events
  TRUCK_POSTED: 'truck_posted',
  TRUCK_VIEWED: 'truck_viewed',
  TRUCK_UPDATED: 'truck_updated',
  TRUCK_DELETED: 'truck_deleted',
  // Quote events
  QUOTE_SUBMITTED: 'quote_submitted',
  QUOTE_ACCEPTED: 'quote_accepted',
  QUOTE_DECLINED: 'quote_declined',
  // Claim invitation events
  CLAIM_INVITATION_SENT: 'claim_invitation_sent',
  CLAIM_INVITATION_ACCEPTED: 'claim_invitation_accepted',
  // Message events
  MESSAGE_SENT: 'message_sent',
  // User events
  USER_REGISTERED: 'user_registered',
  USER_VERIFIED: 'user_verified',
  USER_SUBSCRIPTION_STARTED: 'subscription_started',
  USER_SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
} as const;

export const ANALYTICS_CATEGORIES = {
  LOAD: 'load',
  TRUCK: 'truck',
  QUOTE: 'quote',
  CLAIM: 'claim',
  MESSAGE: 'message',
  USER: 'user',
} as const;

// Sequences table for generating unique identifiers
export const sequences = pgTable("sequences", {
  name: varchar("name", { length: 50 }).primaryKey(),
  currentValue: integer("current_value").notNull().default(0),
});

export type Sequence = typeof sequences.$inferSelect;

// User feedback - temporary beta feedback collection
export const userFeedback = pgTable("user_feedback", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  feedback: text("feedback").notNull(),
  whatTrying: text("what_trying"),
  email: text("email"),
  userId: varchar("user_id").references(() => users.id),
  pageUrl: text("page_url"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("IDX_user_feedback_created_at").on(table.createdAt),
]);

export const insertUserFeedbackSchema = createInsertSchema(userFeedback).omit({
  id: true,
  createdAt: true,
});

export type InsertUserFeedback = z.infer<typeof insertUserFeedbackSchema>;
export type UserFeedback = typeof userFeedback.$inferSelect;

// External parties table for brokers to manage off-platform contacts
export const externalParties = pgTable("external_parties", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  brokerId: varchar("broker_id").notNull().references(() => users.id),
  partyType: text("party_type").notNull(), // 'shipper' or 'carrier'
  companyName: text("company_name").notNull(),
  contactName: text("contact_name"),
  email: text("email"),
  phone: text("phone"),
  dotNumber: text("dot_number"),
  mcNumber: text("mc_number"),
  address: text("address"),
  notes: text("notes"),
  invitationSent: boolean("invitation_sent").default(false),
  invitationSentAt: timestamp("invitation_sent_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("IDX_external_parties_broker_id").on(table.brokerId),
  index("IDX_external_parties_party_type").on(table.partyType),
]);

export const insertExternalPartySchema = createInsertSchema(externalParties).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  invitationSent: true,
  invitationSentAt: true,
});

export const createExternalPartySchema = insertExternalPartySchema.omit({ brokerId: true });

export type InsertExternalParty = z.infer<typeof insertExternalPartySchema>;
export type CreateExternalParty = z.infer<typeof createExternalPartySchema>;
export type ExternalParty = typeof externalParties.$inferSelect;

// Message threads - tracks phase state for carrier-load conversations
// Each carrier gets their own thread with a shipper for a given load
export const messageThreads = pgTable("message_threads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  loadId: varchar("load_id").notNull().references(() => loads.id),
  carrierId: varchar("carrier_id").notNull().references(() => users.id),
  shipperId: varchar("shipper_id").notNull().references(() => users.id),
  // Thread phase: 'pre_quote_question', 'quote_submitted', 'quote_declined', 'active_shipment', 'completed', 'auto_closed'
  threadPhase: text("thread_phase").notNull().default("pre_quote_question"),
  // Reason if carrier declined to quote
  declineReason: text("decline_reason"),
  // Timestamp when thread was closed (declined or auto-closed)
  closedAt: timestamp("closed_at"),
  // Last activity for auto-close timer
  lastActivityAt: timestamp("last_activity_at").defaultNow(),
  // Per-thread dedup flag for the "quote not reviewed" reminder email.
  // Intentionally on message_threads (not loads) so multiple carriers bidding
  // on the same load each get their own reminder independently.
  quoteReminderSent: boolean("quote_reminder_sent").notNull().default(false),
  // Per-thread dedup flag for the 24-hour archive-warning notification/email
  // (fired in the window 144–168 h after last activity, before auto-archive at 168 h).
  archiveWarningSent: boolean("archive_warning_sent").notNull().default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("IDX_message_threads_load_id").on(table.loadId),
  index("IDX_message_threads_carrier_id").on(table.carrierId),
  index("IDX_message_threads_shipper_id").on(table.shipperId),
  index("IDX_message_threads_phase").on(table.threadPhase),
]);

export const insertMessageThreadSchema = createInsertSchema(messageThreads).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  closedAt: true,
  lastActivityAt: true,
});

export type InsertMessageThread = z.infer<typeof insertMessageThreadSchema>;
export type MessageThread = typeof messageThreads.$inferSelect;

// Delivery confirmation tokens — tokenized yes/no links emailed to shippers
export const deliveryConfirmationTokens = pgTable("delivery_confirmation_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  loadId: varchar("load_id").notNull().references(() => loads.id),
  threadId: varchar("thread_id").notNull().references(() => messageThreads.id),
  shipperId: varchar("shipper_id").notNull().references(() => users.id),
  token: text("token").notNull().unique(),
  used: boolean("used").notNull().default(false),
  usedAt: timestamp("used_at"),
  response: text("response"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  expiresAt: timestamp("expires_at").notNull(),
}, (table) => [
  index("IDX_delivery_tokens_token").on(table.token),
  index("IDX_delivery_tokens_load_id").on(table.loadId),
]);

export const insertDeliveryConfirmationTokenSchema = createInsertSchema(deliveryConfirmationTokens).omit({
  id: true,
  createdAt: true,
  usedAt: true,
  used: true,
});

export type InsertDeliveryConfirmationToken = z.infer<typeof insertDeliveryConfirmationTokenSchema>;
export type DeliveryConfirmationToken = typeof deliveryConfirmationTokens.$inferSelect;

// Shipment ratings table
export const shipmentRatings = pgTable("shipment_ratings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  loadId: varchar("load_id").notNull().references(() => loads.id),
  threadId: varchar("thread_id").notNull().references(() => messageThreads.id),
  raterId: varchar("rater_id").notNull().references(() => users.id),
  ratedId: varchar("rated_id").notNull().references(() => users.id),
  rating: integer("rating").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("IDX_shipment_ratings_thread_id").on(table.threadId),
  index("IDX_shipment_ratings_rater_id").on(table.raterId),
  index("IDX_shipment_ratings_rated_id").on(table.ratedId),
  unique("UQ_shipment_ratings_thread_rater").on(table.threadId, table.raterId),
]);

export const insertShipmentRatingSchema = createInsertSchema(shipmentRatings).omit({
  id: true,
  createdAt: true,
});

export type InsertShipmentRating = z.infer<typeof insertShipmentRatingSchema>;
export type ShipmentRating = typeof shipmentRatings.$inferSelect;

// Skipped ratings table
export const skippedRatings = pgTable("skipped_ratings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  threadId: varchar("thread_id").notNull().references(() => messageThreads.id),
  userId: varchar("user_id").notNull().references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("IDX_skipped_ratings_thread_id").on(table.threadId),
  index("IDX_skipped_ratings_user_id").on(table.userId),
  unique("UQ_skipped_ratings_thread_user").on(table.threadId, table.userId),
]);

export const insertSkippedRatingSchema = createInsertSchema(skippedRatings).omit({
  id: true,
  createdAt: true,
});

export type InsertSkippedRating = z.infer<typeof insertSkippedRatingSchema>;
export type SkippedRating = typeof skippedRatings.$inferSelect;

// Admin impersonation audit log
export const impersonationLog = pgTable("impersonation_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  adminUserId: varchar("admin_user_id").notNull(),
  targetUserId: varchar("target_user_id").notNull().references(() => users.id),
  startedAt: timestamp("started_at").defaultNow(),
  endedAt: timestamp("ended_at"),
});

export const insertImpersonationLogSchema = createInsertSchema(impersonationLog).omit({
  id: true,
  startedAt: true,
  endedAt: true,
});

export type InsertImpersonationLog = z.infer<typeof insertImpersonationLogSchema>;
export type ImpersonationLog = typeof impersonationLog.$inferSelect;

// Thread phase constants
export const THREAD_PHASES = {
  PRE_QUOTE_QUESTION: 'pre_quote_question',
  QUOTE_SUBMITTED: 'quote_submitted',
  QUOTE_DECLINED: 'quote_declined',
  ACTIVE_SHIPMENT: 'active_shipment',
  COMPLETED: 'completed',
  AUTO_CLOSED: 'auto_closed',
  CLOSED: 'closed',
} as const;

// Decline reason options
export const DECLINE_REASONS = {
  EQUIPMENT_MISMATCH: 'equipment_mismatch',
  TIMING_DOESNT_WORK: 'timing_doesnt_work',
  PRICING_EXPECTATIONS: 'pricing_expectations',
  OTHER: 'other',
} as const;

// Dismissed notifications - tracks which action items a user has dismissed
export const dismissedNotifications = pgTable("dismissed_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id),
  notificationType: text("notification_type").notNull(),
  loadId: varchar("load_id").notNull(),
  dismissedAt: timestamp("dismissed_at").notNull().defaultNow(),
}, (table) => [
  index("IDX_dismissed_notifications_user_id").on(table.userId),
  index("IDX_dismissed_notifications_lookup").on(table.userId, table.notificationType, table.loadId),
]);

export const insertDismissedNotificationSchema = createInsertSchema(dismissedNotifications).omit({
  id: true,
  dismissedAt: true,
});

export type InsertDismissedNotification = z.infer<typeof insertDismissedNotificationSchema>;
export type DismissedNotification = typeof dismissedNotifications.$inferSelect;

export const siteSettings = pgTable("site_settings", {
  key: varchar("key").primaryKey(),
  value: text("value").notNull(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type SiteSetting = typeof siteSettings.$inferSelect;

// Preferred Partners - members can save other members as preferred partners
export const preferredPartners = pgTable("preferred_partners", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id),
  partnerId: varchar("partner_id").notNull().references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("IDX_preferred_partners_user_id").on(table.userId),
  index("IDX_preferred_partners_partner_id").on(table.partnerId),
  unique("UQ_preferred_partners_user_partner").on(table.userId, table.partnerId),
]);

export const insertPreferredPartnerSchema = createInsertSchema(preferredPartners).omit({
  id: true,
  createdAt: true,
});

export type InsertPreferredPartner = z.infer<typeof insertPreferredPartnerSchema>;
export type PreferredPartner = typeof preferredPartners.$inferSelect;

export const partnerInvites = pgTable("partner_invites", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  shipperId: varchar("shipper_id").notNull().references(() => users.id),
  loadId: varchar("load_id").references(() => loads.id),
  invitedEmail: text("invited_email").notNull(),
  invitedName: text("invited_name").notNull(),
  invitedCompany: text("invited_company"),
  partnerType: text("partner_type").notNull(),
  inviteToken: text("invite_token").unique(),
  inviteTokenExpiresAt: timestamp("invite_token_expires_at"),
  status: text("status").notNull().default("pending"),
  acceptedAt: timestamp("accepted_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  nudge1SentAt: timestamp("nudge1_sent_at"),
  nudge2SentAt: timestamp("nudge2_sent_at"),
}, (table) => [
  index("IDX_partner_invites_shipper_id").on(table.shipperId),
  index("IDX_partner_invites_token").on(table.inviteToken),
  index("IDX_partner_invites_email").on(table.invitedEmail),
  index("IDX_partner_invites_load_id").on(table.loadId),
]);

export const insertPartnerInviteSchema = createInsertSchema(partnerInvites).omit({
  id: true,
  createdAt: true,
});

export type PartnerInvite = typeof partnerInvites.$inferSelect;
export type InsertPartnerInvite = typeof partnerInvites.$inferInsert;

export const loadNotifications = pgTable("load_notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  loadId: varchar("load_id").notNull().references(() => loads.id),
  notifiedUserId: varchar("notified_user_id").references(() => users.id),
  notifiedEmail: text("notified_email").notNull(),
  sentAt: timestamp("sent_at").notNull().defaultNow(),
  source: text("source").notNull().default("partner_invite"),
}, (table) => [
  index("IDX_load_notifications_load_id").on(table.loadId),
  index("IDX_load_notifications_user_id").on(table.notifiedUserId),
]);

export type LoadNotification = typeof loadNotifications.$inferSelect;

export const adminUsers = pgTable("admin_users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  password: text("password").notNull(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  mustChangePassword: boolean("must_change_password").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  lastLoginAt: timestamp("last_login_at"),
});

export const insertAdminUserSchema = createInsertSchema(adminUsers).omit({
  id: true,
  createdAt: true,
  lastLoginAt: true,
});

export type InsertAdminUser = z.infer<typeof insertAdminUserSchema>;
export type AdminUser = typeof adminUsers.$inferSelect;

// Referral Credits — tracks referrer/referred pairs and credit-firing status
export const referralCredits = pgTable("referral_credits", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  referrerId: varchar("referrer_id").notNull().references(() => users.id),
  referredId: varchar("referred_id").notNull().references(() => users.id),
  status: text("status").notNull().default("pending"), // pending | fired | failed
  firedAt: timestamp("fired_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("IDX_referral_credits_referrer_id").on(table.referrerId),
  index("IDX_referral_credits_referred_id").on(table.referredId),
  unique("UQ_referral_credits_referred_id").on(table.referredId),
]);

export const insertReferralCreditSchema = createInsertSchema(referralCredits).omit({
  id: true,
  createdAt: true,
});

export type InsertReferralCredit = z.infer<typeof insertReferralCreditSchema>;
export type ReferralCredit = typeof referralCredits.$inferSelect;
